
import type { RaceEvent, RaceCategory, Athlete, TrackPoint } from '../types';
import { parseTrackFromUrl } from './dataParser';
import { db } from '../firebase';
import { collection, collectionGroup, getDocs, doc, getDoc, writeBatch, query, where, limit, orderBy, updateDoc, DocumentSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { generateUniqueRaceSlug, generateCategorySlug } from '../utils/slugify';

// Check if Firebase is configured with real keys
const isFirebaseConfigured = () => {
    return db?.app?.options?.apiKey?.startsWith("AIza");
};

// Helper to format a parent race event document from Firestore
const formatRaceEventFromFirestore = (data: any, docId?: string): Omit<RaceEvent, 'categories'> => {
    const event: any = { ...data };

    if (docId) {
        event.id = docId;
    }

    // Convert object-based path back to array of arrays
    if (event.course && event.course.path && !Array.isArray(event.course.path[0])) {
        event.course.path = (event.course.path as any[]).map((p: any) => [p.lat, p.lng]);
    }

    // Normalize course.start and course.finish to [lat, lon] tuples
    if (event.course) {
        if (event.course.start && !Array.isArray(event.course.start)) {
            const s = event.course.start;
            event.course.start = [Number(s.lat ?? s.latitude ?? 0), Number(s.lon ?? s.lng ?? s.longitude ?? 0)];
        }
        if (event.course.finish && !Array.isArray(event.course.finish)) {
            const f = event.course.finish;
            event.course.finish = [Number(f.lat ?? f.latitude ?? 0), Number(f.lon ?? f.lng ?? f.longitude ?? 0)];
        }
    }

    return event as Omit<RaceEvent, 'categories'>;
};

// Helper to format a parent race event document for Firestore
const formatRaceEventForFirestore = (event: RaceEvent): any => {
    const { categories, ...parentFields } = JSON.parse(JSON.stringify(event));

    // Convert nested array path to object array [lat, lng] -> {lat, lng}
    if (parentFields.course && parentFields.course.path && Array.isArray(parentFields.course.path)) {
        parentFields.course.path = parentFields.course.path.map((p: number[]) => {
            if (Array.isArray(p) && p.length >= 2) {
                return { lat: p[0], lng: p[1] };
            }
            return p;
        });
    }

    // Ensure course.start and course.finish are saved as [number, number] arrays
    if (parentFields.course) {
        if (Array.isArray(parentFields.course.start) && parentFields.course.start.length >= 2) {
            parentFields.course.start = [Number(parentFields.course.start[0]), Number(parentFields.course.start[1])];
        }
        if (Array.isArray(parentFields.course.finish) && parentFields.course.finish.length >= 2) {
            parentFields.course.finish = [Number(parentFields.course.finish[0]), Number(parentFields.course.finish[1])];
        }
    }

    parentFields.categoryCount = event.categories.length;
    return parentFields;
};

// Helper to format a category subdocument for Firestore (strips track data)
const formatCategoryForFirestore = (category: RaceCategory): any => {
    const catCopy = JSON.parse(JSON.stringify(category));

    if (catCopy.athletes && Array.isArray(catCopy.athletes)) {
        catCopy.athletes = catCopy.athletes.map((a: Athlete) => {
            if (!a) return null;
            const { track, ...leanAthlete } = a;
            return leanAthlete;
        }).filter((a: any) => a !== null);
    }

    return catCopy;
};

export interface GroupRideOccurrenceSummary {
    event: RaceEvent; // parent fields only; categories not loaded
    categoryId: string;
    riderCount: number;
    riderUserIds: string[];
}

export const raceEventService = {
    /**
     * Lists per-date occurrences of a recurring group ride, newest first.
     * Reads each occurrence's category for rider counts only — tracks are NOT rehydrated.
     */
    getGroupRideOccurrences: async (seriesId: string, maxResults = 12): Promise<GroupRideOccurrenceSummary[]> => {
        if (!isFirebaseConfigured()) return [];

        try {
            const q = query(
                collection(db, "races"),
                where("seriesId", "==", seriesId),
                orderBy("occurrenceDate", "desc"),
                limit(maxResults)
            );
            const snap = await getDocs(q);

            return await Promise.all(snap.docs.map(async (docSnap) => {
                const event = formatRaceEventFromFirestore(docSnap.data(), docSnap.id);
                const catSnap = await getDocs(collection(db, "races", docSnap.id, "categories"));
                const catDoc = catSnap.docs[0];
                const athletes: any[] = catDoc?.data()?.athletes || [];
                return {
                    event: { ...event, categories: [] },
                    categoryId: catDoc?.data()?.slug || catDoc?.id || 'group-ride',
                    riderCount: athletes.length,
                    riderUserIds: athletes.map(a => a.userId || a.id).filter(Boolean),
                };
            }));
        } catch (error) {
            console.error("Failed to fetch group ride occurrences", error);
            return [];
        }
    },

    /**
     * Fetches all race events (parent docs only, new structure).
     * Categories are NOT loaded — use getRaceEventById for full data.
     */
    getAllRaceEvents: async (): Promise<RaceEvent[]> => {
        if (!isFirebaseConfigured()) {
            return [];
        }

        try {
            const querySnapshot = await getDocs(collection(db, "races"));
            const events: RaceEvent[] = [];

            querySnapshot.forEach((docSnap) => {
                const event = formatRaceEventFromFirestore(docSnap.data(), docSnap.id);
                events.push({ ...event, categories: [] });
            });

            return events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        } catch (error) {
            console.error("Failed to fetch race events from Firebase", error);
            return [];
        }
    },

    /**
     * Fetches a single race event by slug or ID, including all categories with rehydrated tracks.
     */
    getRaceEventBySlug: async (slugOrId: string): Promise<RaceEvent | undefined> => {
        if (!isFirebaseConfigured()) {
            return undefined;
        }

        try {
            let docSnap: DocumentSnapshot | undefined = undefined;
            
            // First try by slug
            const q = query(collection(db, "races"), where("slug", "==", slugOrId), limit(1));
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
                docSnap = querySnapshot.docs[0];
            } else {
                // Fallback to ID
                const docRef = doc(db, "races", slugOrId);
                docSnap = await getDoc(docRef);
            }

            if (!docSnap || !docSnap.exists()) return undefined;

            const event = formatRaceEventFromFirestore(docSnap.data(), docSnap.id);

            // Fetch categories subcollection
            const catSnapshot = await getDocs(collection(db, "races", docSnap.id, "categories"));
            const categories: RaceCategory[] = [];

            catSnapshot.forEach((catDoc) => {
                const catData = catDoc.data();
                categories.push({
                    ...catData,
                    id: catDoc.id,
                } as RaceCategory);
            });

            // Rehydrate tracks for all athletes in all categories
            const hydratedCategories = await Promise.all(categories.map(async (cat) => {
                if (cat.athletes && cat.athletes.length > 0) {
                    const hydratedAthletes = await Promise.all(cat.athletes.map(async (athlete) => {
                        if (athlete.fileUrl && (!athlete.track || athlete.track.length === 0)) {
                            try {
                                const { track } = await parseTrackFromUrl(athlete.fileUrl);
                                return { ...athlete, track };
                            } catch (err) {
                                console.error(`Failed to load track for ${athlete.name}`, err);
                                return { ...athlete, track: [] };
                            }
                        }
                        return athlete;
                    }));
                    return { ...cat, athletes: hydratedAthletes };
                }
                return cat;
            }));

            return { ...event, categories: hydratedCategories };
        } catch (error) {
            console.error("Failed to fetch race event from Firebase", error);
            return undefined;
        }
    },

    /**
     * Saves a race event with all its categories using a batched write.
     * Writes parent doc + each category as a subcollection doc.
     */
    saveRaceEvent: async (event: RaceEvent): Promise<void> => {
        if (!event) {
            throw new Error("Cannot save race event: Data is missing.");
        }
        if (!event.id) {
            throw new Error("Cannot save race event: ID is undefined.");
        }

        if (!isFirebaseConfigured()) {
            throw new Error("Firebase is not configured.");
        }

        try {
            const batch = writeBatch(db);

            // Generate slug if it doesn't exist
            if (!event.slug) {
                event.slug = await generateUniqueRaceSlug(event.name);
            }

            // Write parent doc
            const parentData = formatRaceEventForFirestore(event);
            const parentRef = doc(db, "races", String(event.id));
            batch.set(parentRef, parentData);

            // Fetch existing categories to delete any that were removed
            const existingCatsSnap = await getDocs(collection(db, "races", String(event.id), "categories"));
            const incomingCatIds = new Set(event.categories.map(c => String(c.id)));

            existingCatsSnap.forEach((catDoc) => {
                if (!incomingCatIds.has(catDoc.id)) {
                    batch.delete(catDoc.ref);
                }
            });

            // Write each category as a subcollection doc
            for (const category of event.categories) {
                if (!category.id) {
                    throw new Error(`Cannot save category without ID in event ${event.id}`);
                }
                
                if (!category.slug) {
                    category.slug = generateCategorySlug(category.name);
                }

                const catData = formatCategoryForFirestore(category);
                const catRef = doc(db, "races", String(event.id), "categories", String(category.id));
                batch.set(catRef, catData);
            }

            await batch.commit();
        } catch (error) {
            console.error("Failed to save race event to Firebase", error);
            throw error;
        }
    },

    /**
     * Updates only the athletes array for a specific category.
     * Useful for regular users uploading their own data without needing full write permissions.
     */
    updateCategoryData: async (raceId: string, categoryId: string, updates: Partial<RaceCategory>): Promise<void> => {
        if (!isFirebaseConfigured()) return;
        
        try {
            const catRef = doc(db, "races", raceId, "categories", categoryId);
            
            const payload: any = { ...updates };
            
            if (updates.athletes) {
                // Strip track data before saving
                const leanAthletes = updates.athletes.map((a: Athlete) => {
                    if (!a) return null;
                    const { track, ...leanAthlete } = a;
                    return leanAthlete;
                }).filter((a: any) => a !== null);
                payload.athletes = leanAthletes;
            }
            
            // Firestore does not allow `undefined` values. 
            // JSON stringify/parse automatically strips undefined properties just like the batch operations do.
            const cleanPayload = JSON.parse(JSON.stringify(payload));
            
            await updateDoc(catRef, cleanPayload);
        } catch (error) {
            console.error("Failed to update category data", error);
            throw error;
        }
    },

    /**
     * Looks up the parent race + category for a legacy category doc ID.
     * Pre-migration, each race/category combo had its own top-level doc.
     * Post-migration, those old doc IDs are preserved on the subcollection
     * category doc via `_originalDocId`. Returns `{ parentId, categoryId }`
     * if a match is found so callers can redirect old URLs.
     */
    findParentByLegacyCategoryId: async (legacyId: string): Promise<{ parentId: string; categoryId: string } | null> => {
        if (!isFirebaseConfigured() || !legacyId) return null;

        try {
            const q = query(
                collectionGroup(db, "categories"),
                where("_originalDocId", "==", legacyId),
                limit(1)
            );
            const snap = await getDocs(q);
            if (snap.empty) return null;

            const match = snap.docs[0];
            const parentId = match.ref.parent.parent?.id;
            if (!parentId) return null;

            return { parentId, categoryId: match.id };
        } catch (error) {
            console.error("Failed legacy category lookup", error);
            return null;
        }
    },

    /**
     * Deletes a race event and all its category subdocuments.
     */
    deleteRaceEvent: async (raceId: string): Promise<void> => {
        if (!raceId) {
            throw new Error("Cannot delete race event: Missing ID");
        }

        if (!isFirebaseConfigured()) {
            throw new Error("Firebase is not configured.");
        }

        try {
            // First, delete all category subdocuments
            const catSnapshot = await getDocs(collection(db, "races", raceId, "categories"));
            const batch = writeBatch(db);

            catSnapshot.forEach((catDoc) => {
                batch.delete(catDoc.ref);
            });

            // Delete the parent document
            batch.delete(doc(db, "races", raceId));

            await batch.commit();
        } catch (error) {
            console.error("Failed to delete race event from Firebase", error);
            throw error;
        }
    },

    /**
     * Resolves the IANA time zone for given coordinates via Cloud Functions,
     * falling back to the browser's local time zone if offline/error.
     */
    getTimeZoneForLocation: async (lat: number, lon: number): Promise<string> => {
        try {
            const functions = getFunctions();
            const getTimeZoneFn = httpsCallable<{ lat: number; lon: number }, { timeZone: string }>(functions, 'getTimeZoneForLocation');
            const result = await getTimeZoneFn({ lat, lon });
            if (result.data?.timeZone) {
                return result.data.timeZone;
            }
        } catch (error) {
            console.warn("Failed to get time zone from backend, falling back to browser zone:", error);
        }
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
    },
};
